// src/features/my-shift/MyShiftPanel.tsx
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { addMonths, subMonths, addWeeks, subWeeks } from "date-fns";
import { todayDateInTimezone, todayInTimezone } from "@/features/shift/today";
import { formatMonthDay, formatMonthYear, formatWeekdayMonthDay } from "@/i18n/dateFormat";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import type { ShiftRequestCreateData } from "@/features/shift-request/types";
import { shiftRequestApi } from "@/features/shift-request/api";
import { useMyShiftData } from "./hooks/useMyShiftData";
import { getMonthRange, getWeekRange } from "./calendar";
import { MonthGrid } from "./components/MonthGrid";
import { WeekList } from "./components/WeekList";
import { DayDetail } from "./components/DayDetail";
import { MyRequestsTable } from "./components/MyRequestsTable";

type View = "month" | "week";
type Tab = "calendar" | "requests";

interface MyShiftPanelProps {
  /** 追加到 header 容器的类;弹窗壳用 pr-12 给 Dialog 右上角关闭按钮让位。 */
  headerClassName?: string;
}

export function MyShiftPanel({ headerClassName }: MyShiftPanelProps = {}) {
  const { t, i18n } = useTranslation('shifts')
  const tz = useUserTimezone();
  const [tab, setTab] = useState<Tab>("calendar");
  const [view, setView] = useState<View>("month");
  const [anchor, setAnchor] = useState<Date>(() => todayDateInTimezone(tz));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const range = useMemo(
    () => (view === "month" ? getMonthRange(anchor) : getWeekRange(anchor)),
    [view, anchor],
  );
  const { byDate, requests, pendingDates, loading, refetch } = useMyShiftData(range.start, range.end);

  const selectedSchedule = selectedDate ? byDate[selectedDate] ?? null : null;
  const selectedRequests = useMemo(
    () => (selectedSchedule ? requests.filter((r) => (r.scheduleId || r.schedule_id) === selectedSchedule.id) : []),
    [requests, selectedSchedule],
  );
  const dateLabel = selectedDate ? formatWeekdayMonthDay(new Date(`${selectedDate}T12:00:00`), i18n.language) : "";

  const navPrev = () => setAnchor((d) => (view === "month" ? subMonths(d, 1) : subWeeks(d, 1)));
  const navNext = () => setAnchor((d) => (view === "month" ? addMonths(d, 1) : addWeeks(d, 1)));
  const goToday = () => { setAnchor(todayDateInTimezone(tz)); setSelectedDate(todayInTimezone(tz)); };

  const submitRequest = async (data: ShiftRequestCreateData) => {
    await shiftRequestApi.createShiftRequest(data); // 错误由 DayDetail 捕获 toast
    toast.success(data.request_type === "leave" ? t('myShift.toast.leaveSubmitted') : t('myShift.toast.changeSubmitted'));
    void refetch();
  };
  const cancelRequest = async (id: string) => {
    try {
      await shiftRequestApi.cancelRequest(id);
      toast.success(t('myShift.toast.requestCancelled'));
      void refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.msg || err?.response?.data?.message || err?.message || t('myShift.toast.failedToCancel'));
    }
  };

  const headerLabel = view === "month"
    ? formatMonthYear(anchor, i18n.language)
    : t('myShift.weekOf', { date: formatMonthDay(new Date(`${range.start}T12:00:00`), i18n.language) });

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <div className={cn("flex items-center justify-between gap-3 border-b border-border px-4 py-2.5", headerClassName)}>
        <div className="flex items-center gap-4">
          <div className="text-base font-extrabold">{t('myShift.title')}</div>
          <div role="tablist" className="flex gap-0.5 rounded-lg bg-muted p-0.5">
            {(["calendar", "requests"] as Tab[]).map((tabKey) => (
              <button key={tabKey} type="button" role="tab" aria-selected={tab === tabKey} onClick={() => setTab(tabKey)}
                className={cn("rounded-md px-3 py-1 text-[12.5px] font-semibold", tab === tabKey ? "bg-background text-purple-700 shadow-sm" : "text-muted-foreground")}>
                {tabKey === "calendar" ? t('myShift.tab.calendar') : t('myShift.tab.requests')}
              </button>
            ))}
          </div>
        </div>
        {tab === "calendar" && (
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 rounded-lg bg-muted p-0.5">
              {(["month", "week"] as View[]).map((v) => (
                <button key={v} type="button" aria-pressed={view === v} onClick={() => { setView(v); setSelectedDate(null); }}
                  className={cn("rounded-md px-2.5 py-1 text-[11.5px] font-semibold capitalize", view === v ? "bg-purple-600 text-white" : "text-muted-foreground")}>
                  {t(`myShift.view.${v}`)}
                </button>
              ))}
            </div>
            <button type="button" aria-label={t('myShift.nav.previous')} onClick={navPrev} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent"><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-[110px] text-center text-[13px] font-bold">{headerLabel}</span>
            <button type="button" aria-label={t('myShift.nav.next')} onClick={navNext} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent"><ChevronRight className="h-4 w-4" /></button>
            <button type="button" onClick={goToday} className="rounded-md border border-border px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground hover:bg-accent">{t('myShift.nav.today')}</button>
          </div>
        )}
      </div>

      {/* body */}
      {tab === "calendar" ? (
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-auto p-4">
            {loading && (
              <div className="flex justify-center py-10">
                <div className="h-7 w-7 animate-spin rounded-full border-4 border-purple-200 dark:border-purple-800 border-t-purple-600" />
              </div>
            )}
            {!loading && view === "month" && (
              <MonthGrid anchor={anchor} byDate={byDate} pendingDates={pendingDates} selectedDate={selectedDate} timezone={tz} onSelectDate={setSelectedDate} />
            )}
            {!loading && view === "week" && (
              <WeekList anchor={anchor} byDate={byDate} pendingDates={pendingDates} selectedDate={selectedDate} timezone={tz} onSelectDate={setSelectedDate} />
            )}
          </div>
          <div className="w-[320px] flex-none border-l border-border bg-muted/40">
            <DayDetail schedule={selectedSchedule} dateLabel={dateLabel} requests={selectedRequests} timezone={tz} onSubmit={submitRequest} onCancelRequest={cancelRequest} />
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <MyRequestsTable />
        </div>
      )}
    </div>
  );
}
